import React, { useEffect, useMemo, useState } from 'react';
import WeatherCard from './WeatherCard';
import SurfMap from './SurfMap';
import { buildForecastDays, buildHourlyConditionsForDate } from './utils';

const copy = {
  english: {
    eyebrow: 'West coast surf report',
    title: 'Apelviken, distilled.',
    description:
      'A quieter surf dashboard focused on the essentials: weather, wetsuit choice, and wind orientation for the break.',
  },
  swedish: {
    eyebrow: 'Surföversikt på västkusten',
    title: 'Apelviken, avskalat.',
    description:
      'En lugnare surfvy med det viktigaste: väder, våtdräktsval och vindens riktning för surfplatsen.',
  },
};

const HomeContent = ({ spotData, seaTemperature, language }) => {
  const text = copy[language] || copy.english;
  const forecastDays = useMemo(() => buildForecastDays(spotData?.weather), [spotData]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHourIndex, setSelectedHourIndex] = useState(0);

  const selectedDay = useMemo(
    () => forecastDays.find((day) => day.time === selectedDate) || null,
    [forecastDays, selectedDate]
  );

  const hourlyConditions = useMemo(
    () => buildHourlyConditionsForDate(spotData, selectedDate),
    [spotData, selectedDate]
  );

  useEffect(() => {
    setSelectedHourIndex(0);
  }, [selectedDate]);

  useEffect(() => {
    if (selectedDate || !forecastDays.length) {
      return;
    }

    setSelectedDate(forecastDays[0].time);
  }, [forecastDays, selectedDate]);

  return (
    <>
      <section className="hero panel panel-hero">
        <p className="eyebrow">{text.eyebrow}</p>
        <h1>{text.title}</h1>
        <p className="hero-copy">{text.description}</p>
      </section>

      <div className="dashboard-grid">
        <SurfMap
          spotData={spotData}
          seaTemperature={seaTemperature}
          language={language}
          selectedDay={selectedDay}
          hourlyConditions={hourlyConditions}
          selectedHourIndex={selectedHourIndex}
          onHourChange={setSelectedHourIndex}
        />
        <WeatherCard
          weatherData={spotData?.weather}
          seaTemperature={seaTemperature}
          language={language}
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
        />
      </div>

      <section className="panel panel-video">
        <div className="video-block">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">{language === 'english' ? 'Video' : 'Video'}</p>
              <h2>{language === 'english' ? 'View from the Stormhouse' : 'Vy från Stormhuset'}</h2>
            </div>
          </div>

          <div className="video-embed">
            <iframe
              src="https://www.youtube.com/embed/7TVhopVz3Gg"
              title="Apelviken surf video"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </div>

        <div className="video-block">
          <div className="panel-heading">
            <div>
              <h2>{language === 'english' ? 'View of Apelviken Bay' : 'Vy över Apelvikenbukten'}</h2>
            </div>
          </div>

          <div className="video-embed">
            <iframe
              src="https://www.youtube.com/embed/vQ3IGhbe8xk"
              title="View from Restaurant Solviken in Apelviken"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default HomeContent;
